import { Controller } from "@hotwired/stimulus"

/**
 * DualPanelSelectorController - Gestion de sélection à deux colonnes
 *
 * Panel gauche: items disponibles (avec recherche et pagination)
 * Panel droite: items sélectionnés (avec drag & drop pour réordonnement)
 *
 * Usage:
 * <div data-controller="dual-panel-selector"
 *      data-dual-panel-selector-url-value="/api/items/search"
 *      data-dual-panel-selector-input-name-value="model[item_ids][]">
 */
export default class extends Controller {
  static targets = [
    "search",
    "availableList",
    "selectedList",
    "pagination",
    "availableCount",
    "selectedCount",
    "hiddenInputs",
    "liveRegion",
    "loading",
    "emptyAvailable",
    "emptySelected"
  ]

  static values = {
    url: String,
    inputName: { type: String, default: "items[]" },
    selectedIds: { type: Array, default: [] },
    selectedItems: { type: Array, default: [] },
    itemsPerPage: { type: Number, default: 20 },
    currentPage: { type: Number, default: 1 },
    totalPages: { type: Number, default: 1 },
    totalCount: { type: Number, default: 0 },
    debounceMs: { type: Number, default: 300 },
    confirmClearThreshold: { type: Number, default: 5 },
    // Textes UI (aria-live) — defaults FR, surchargeables via data-dual-panel-selector-*-value
    searchErrorText: { type: String, default: "Erreur lors de la recherche" },
    itemFallbackText: { type: String, default: "Élément" },
    itemAddedText: { type: String, default: "ajouté" },
    itemRemovedText: { type: String, default: "retiré" },
    clearAllConfirmText: { type: String, default: "Voulez-vous vraiment retirer les %{count} éléments sélectionnés ?" }
  }

  connect() {
    this.debouncedSearch = this.debounce(this.performSearch.bind(this), this.debounceValue)
    this.performSearch()
  }

  // ==================== Recherche ====================

  onSearchInput() {
    this.currentPageValue = 1
    this.debouncedSearch()
  }

  async performSearch() {
    const query = this.hasSearchTarget ? this.searchTarget.value.trim() : ""

    this.showLoading()

    try {
      const params = new URLSearchParams({
        q: query,
        page: this.currentPageValue,
        per_page: this.itemsPerPageValue,
        "exclude_ids[]": this.selectedIdsValue.join(",")
      })

      // Ajouter exclude_ids comme paramètres séparés pour Rails
      this.selectedIdsValue.forEach(id => {
        params.append("exclude_ids[]", id)
      })

      const response = await fetch(`${this.urlValue}?${params}`, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        }
      })

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

      const data = await response.json()
      this.handleSearchResponse(data)
    } catch (error) {
      console.error("Erreur recherche:", error)
      this.announce(this.searchErrorTextValue)
    } finally {
      this.hideLoading()
    }
  }

  handleSearchResponse(data) {
    // À surcharger dans les sous-classes pour le rendu spécifique
    this.totalPagesValue = data.pagination?.total_pages || 1
    this.totalCountValue = data.pagination?.total_count || 0

    this.renderAvailableItems(data)
    this.renderPagination()
    this.updateAvailableCount()

    const count = data.pagination?.total_count || 0
    this.announce(`${count} résultat${count > 1 ? "s" : ""} trouvé${count > 1 ? "s" : ""}`)
  }

  // ==================== Sélection/Désélection ====================

  toggle(event) {
    const itemElement = event.currentTarget.closest("[data-item-id]")
    if (!itemElement) return

    const itemId = parseInt(itemElement.dataset.itemId)
    const isSelected = this.selectedIdsValue.includes(itemId)

    if (isSelected) {
      this.removeFromSelection(itemId)
    } else {
      this.addToSelection(itemId, itemElement)
    }
  }

  addToSelection(itemId, itemElement) {
    // Extraire les données de l'élément
    const itemData = this.extractItemData(itemElement)

    // Ajouter aux valeurs
    this.selectedIdsValue = [...this.selectedIdsValue, itemId]
    this.selectedItemsValue = [...this.selectedItemsValue, itemData]

    // Mettre à jour l'UI
    this.renderSelectedItem(itemData)
    this.removeFromAvailableList(itemId)
    this.updateCounts()
    this.updateHiddenInputs()

    this.announce(`${itemData.name || this.itemFallbackTextValue} ${this.itemAddedTextValue}`)
  }

  removeFromSelection(itemId) {
    const item = this.selectedItemsValue.find(i => i.id === itemId)

    // Retirer des valeurs
    this.selectedIdsValue = this.selectedIdsValue.filter(id => id !== itemId)
    this.selectedItemsValue = this.selectedItemsValue.filter(i => i.id !== itemId)

    // Mettre à jour l'UI
    this.removeFromSelectedList(itemId)
    this.updateCounts()
    this.updateHiddenInputs()

    // Rafraîchir la liste disponible pour montrer l'item retiré
    this.performSearch()

    this.announce(`${item?.name || this.itemFallbackTextValue} ${this.itemRemovedTextValue}`)
  }

  clearAll() {
    if (this.selectedIdsValue.length > this.confirmClearThresholdValue) {
      const message = this.clearAllConfirmTextValue.replace("%{count}", this.selectedIdsValue.length)
      if (!confirm(message)) {
        return
      }
    }

    const count = this.selectedIdsValue.length
    this.selectedIdsValue = []
    this.selectedItemsValue = []

    if (this.hasSelectedListTarget) {
      this.selectedListTarget.innerHTML = ""
    }

    this.updateCounts()
    this.updateHiddenInputs()
    this.performSearch()
    this.renderEmptySelected()

    this.announce(`${count} élément${count > 1 ? "s" : ""} retiré${count > 1 ? "s" : ""}`)
  }

  // ==================== Pagination ====================

  goToPage(event) {
    const page = parseInt(event.currentTarget.dataset.page)
    if (page < 1 || page > this.totalPagesValue) return

    this.currentPageValue = page
    this.performSearch()
  }

  previousPage() {
    if (this.currentPageValue > 1) {
      this.currentPageValue--
      this.performSearch()
    }
  }

  nextPage() {
    if (this.currentPageValue < this.totalPagesValue) {
      this.currentPageValue++
      this.performSearch()
    }
  }

  // ==================== Rendu ====================

  renderAvailableItems(data) {
    // À surcharger dans les sous-classes
    if (!this.hasAvailableListTarget) return

    const items = this.getItemsFromResponse(data)

    if (items.length === 0) {
      this.renderEmptyAvailable()
      return
    }

    this.availableListTarget.innerHTML = items
      .map(item => this.renderAvailableItemHTML(item))
      .join("")
  }

  renderAvailableItemHTML(item) {
    // À surcharger dans les sous-classes
    return `
      <button type="button"
              class="dual-panel-item"
              data-item-id="${item.id}"
              data-action="click->dual-panel-selector#toggle"
              role="option"
              aria-selected="false"
              tabindex="0">
        <span class="dual-panel-item-name">${this.escapeHtml(item.name)}</span>
      </button>
    `
  }

  renderSelectedItem(item) {
    if (!this.hasSelectedListTarget) return

    // Cacher le message vide
    if (this.hasEmptySelectedTarget) {
      this.emptySelectedTarget.classList.add("hidden")
    }

    const html = this.renderSelectedItemHTML(item)
    this.selectedListTarget.insertAdjacentHTML("beforeend", html)
  }

  renderSelectedItemHTML(item) {
    // À surcharger dans les sous-classes
    return `
      <div class="dual-panel-selected-item"
           data-item-id="${item.id}"
           data-sortable-id="${item.id}"
           role="option"
           aria-selected="true"
           tabindex="0">
        <i class="fa-solid fa-grip-lines handle cursor-move text-gray-400 mr-2" aria-hidden="true"></i>
        <span class="flex-1">${this.escapeHtml(item.name)}</span>
        <button type="button"
                class="text-red-500 hover:text-red-700"
                data-action="click->dual-panel-selector#toggle"
                aria-label="Retirer ${this.escapeHtml(item.name)}"
                tabindex="-1">
          <i class="fa-solid fa-times" aria-hidden="true"></i>
        </button>
      </div>
    `
  }

  renderPagination() {
    if (!this.hasPaginationTarget || this.totalPagesValue <= 1) {
      if (this.hasPaginationTarget) {
        this.paginationTarget.innerHTML = ""
      }
      return
    }

    const pages = this.getPaginationPages()
    let html = `
      <button type="button"
              class="pagination-btn ${this.currentPageValue === 1 ? "disabled" : ""}"
              data-action="click->dual-panel-selector#previousPage"
              ${this.currentPageValue === 1 ? "disabled" : ""}>
        <i class="fa-solid fa-chevron-left"></i>
      </button>
    `

    pages.forEach(page => {
      if (page === "...") {
        html += `<span class="pagination-ellipsis">...</span>`
      } else {
        html += `
          <button type="button"
                  class="pagination-btn ${page === this.currentPageValue ? "active" : ""}"
                  data-action="click->dual-panel-selector#goToPage"
                  data-page="${page}">
            ${page}
          </button>
        `
      }
    })

    html += `
      <button type="button"
              class="pagination-btn ${this.currentPageValue === this.totalPagesValue ? "disabled" : ""}"
              data-action="click->dual-panel-selector#nextPage"
              ${this.currentPageValue === this.totalPagesValue ? "disabled" : ""}>
        <i class="fa-solid fa-chevron-right"></i>
      </button>
    `

    this.paginationTarget.innerHTML = html
  }

  renderEmptyAvailable() {
    if (this.hasEmptyAvailableTarget) {
      this.emptyAvailableTarget.classList.remove("hidden")
    }
    if (this.hasAvailableListTarget) {
      this.availableListTarget.innerHTML = ""
    }
  }

  renderEmptySelected() {
    if (this.hasEmptySelectedTarget) {
      this.emptySelectedTarget.classList.remove("hidden")
    }
  }

  // ==================== Gestion des inputs cachés ====================

  updateHiddenInputs() {
    if (!this.hasHiddenInputsTarget) return

    this.hiddenInputsTarget.innerHTML = this.selectedItemsValue
      .map((item, index) => this.renderHiddenInputsForItem(item, index))
      .join("")
  }

  renderHiddenInputsForItem(item, index) {
    // À surcharger pour des inputs spécifiques
    return `<input type="hidden" name="${this.inputNameValue}" value="${item.id}">`
  }

  // ==================== Helpers ====================

  removeFromAvailableList(itemId) {
    const element = this.availableListTarget?.querySelector(`[data-item-id="${itemId}"]`)
    element?.remove()
  }

  removeFromSelectedList(itemId) {
    const element = this.selectedListTarget?.querySelector(`[data-item-id="${itemId}"]`)
    element?.remove()

    if (this.selectedIdsValue.length === 0) {
      this.renderEmptySelected()
    }
  }

  updateCounts() {
    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = this.selectedIdsValue.length
    }
    this.updateAvailableCount()
  }

  updateAvailableCount() {
    if (this.hasAvailableCountTarget) {
      this.availableCountTarget.textContent = this.totalCountValue
    }
  }

  extractItemData(element) {
    // À surcharger pour extraire les données spécifiques
    return {
      id: parseInt(element.dataset.itemId),
      name: element.dataset.itemName || element.textContent.trim()
    }
  }

  getItemsFromResponse(data) {
    // À surcharger selon le format de réponse API
    return data.items || data.modules || data.students || []
  }

  getPaginationPages() {
    const current = this.currentPageValue
    const total = this.totalPagesValue
    const pages = []

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i)
    } else {
      pages.push(1)
      if (current > 3) pages.push("...")
      for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
        pages.push(i)
      }
      if (current < total - 2) pages.push("...")
      pages.push(total)
    }

    return pages
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
    }
  }

  // ==================== Accessibilité ====================

  announce(message) {
    if (this.hasLiveRegionTarget) {
      this.liveRegionTarget.textContent = message
    }
  }

  handleKeydown(event) {
    const activeElement = document.activeElement
    const isInAvailableList = activeElement?.closest("[data-dual-panel-selector-target='availableList']") ||
                              activeElement?.closest("[data-module-selector-target='availableList']") ||
                              activeElement?.closest("[data-participant-selector-target='availableList']")
    const isInSelectedList = activeElement?.closest("[data-dual-panel-selector-target='selectedList']") ||
                             activeElement?.closest("[data-module-selector-target='selectedList']") ||
                             activeElement?.closest("[data-participant-selector-target='selectedList']")

    switch (event.key) {
      case "Enter":
      case " ":
        if (activeElement?.dataset?.itemId) {
          event.preventDefault()
          this.toggle(event)
        }
        break

      case "Delete":
      case "Backspace":
        if (isInSelectedList && activeElement?.dataset?.itemId) {
          event.preventDefault()
          const nextElement = this.getNextFocusableItem(activeElement, isInSelectedList ? "selected" : "available")
          this.toggle(event)
          nextElement?.focus()
        }
        break

      case "/":
        if (!event.ctrlKey && !event.metaKey && !this.isInputFocused()) {
          event.preventDefault()
          this.searchTarget?.focus()
        }
        break

      case "ArrowDown":
        if (isInAvailableList || isInSelectedList) {
          event.preventDefault()
          this.focusNextItem(activeElement, isInAvailableList ? "available" : "selected")
        }
        break

      case "ArrowUp":
        if (isInAvailableList || isInSelectedList) {
          event.preventDefault()
          this.focusPreviousItem(activeElement, isInAvailableList ? "available" : "selected")
        }
        break

      case "ArrowRight":
        // Passer de la liste disponible à la liste sélectionnée
        if (isInAvailableList) {
          event.preventDefault()
          this.focusFirstItemInList("selected")
        }
        break

      case "ArrowLeft":
        // Passer de la liste sélectionnée à la liste disponible
        if (isInSelectedList) {
          event.preventDefault()
          this.focusFirstItemInList("available")
        }
        break

      case "Home":
        if (isInAvailableList || isInSelectedList) {
          event.preventDefault()
          this.focusFirstItemInList(isInAvailableList ? "available" : "selected")
        }
        break

      case "End":
        if (isInAvailableList || isInSelectedList) {
          event.preventDefault()
          this.focusLastItemInList(isInAvailableList ? "available" : "selected")
        }
        break

      case "Escape":
        // Retirer le focus et revenir à la recherche
        if (isInAvailableList || isInSelectedList) {
          event.preventDefault()
          this.searchTarget?.focus()
        }
        break
    }
  }

  isInputFocused() {
    const tag = document.activeElement?.tagName?.toLowerCase()
    return tag === "input" || tag === "textarea"
  }

  getListTarget(listType) {
    if (listType === "available") {
      return this.hasAvailableListTarget ? this.availableListTarget : null
    }
    return this.hasSelectedListTarget ? this.selectedListTarget : null
  }

  getFocusableItems(listType) {
    const list = this.getListTarget(listType)
    if (!list) return []
    return Array.from(list.querySelectorAll("[data-item-id]"))
  }

  focusNextItem(currentElement, listType) {
    const items = this.getFocusableItems(listType)
    if (items.length === 0) return

    const currentIndex = items.indexOf(currentElement)
    const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
    items[nextIndex]?.focus()
  }

  focusPreviousItem(currentElement, listType) {
    const items = this.getFocusableItems(listType)
    if (items.length === 0) return

    const currentIndex = items.indexOf(currentElement)
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1
    items[prevIndex]?.focus()
  }

  focusFirstItemInList(listType) {
    const items = this.getFocusableItems(listType)
    items[0]?.focus()
  }

  focusLastItemInList(listType) {
    const items = this.getFocusableItems(listType)
    items[items.length - 1]?.focus()
  }

  getNextFocusableItem(currentElement, listType) {
    const items = this.getFocusableItems(listType)
    const currentIndex = items.indexOf(currentElement)

    // Prochaine priorité: item suivant, puis précédent, puis premier de l'autre liste
    if (currentIndex < items.length - 1) {
      return items[currentIndex + 1]
    } else if (currentIndex > 0) {
      return items[currentIndex - 1]
    }
    return null
  }

  // ==================== Utilitaires ====================

  debounce(func, wait) {
    let timeout
    return (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func.apply(this, args), wait)
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
